import type { GraphData, EdgeConfig } from '@antv/g6'
import { Util } from '@antv/g6'
import type { UserJourneyEdge, UserJourneyNode } from './type'

// see: https://g6-v4.antv.vision/api/graph-layout/dagre#layoutcfgranksep
export const NODE_RANKSEP = 96

export enum GraphMode {
  Default = 'default',
  Readonly = 'readonly',
}

// see: https://g6-v4.antv.vision/design/global/style#%E4%BA%A4%E4%BA%92%E6%A0%B7%E5%BC%8F-1
export enum GraphStates {
  Hover = 'hover',
  Actived = 'actived',
  Selected = 'selected',
  Highlight = 'highlight',
}

export function resolveGraphData(nodes: UserJourneyNode[]): GraphData {
  const graphNodes: GraphData['nodes'] = nodes.map((node) => ({
    id: node.id,
    data: node,

    // see: https://g6-v4.antv.vision/manual/middle/elements/nodes/anchorpoint
    anchorPoints: [
      [0, 0.5],
      [1, 0.5],
    ],
  }))

  const graphEdges: GraphData['edges'] = nodes.reduce<
    Required<GraphData>['edges']
  >(
    (acc, node) => [
      ...acc,
      ...(node.edges?.map<EdgeConfig>((id) => ({
        source: node.id,
        target: id,

        // 该边连入 source 点的第 1 个 anchorPoint，
        sourceAnchor: 1,
        // 该边连入 source 点的第 2 个 anchorPoint，
        targetAnchor: 0,
      })) ?? []),
    ],
    [],
  )

  return {
    nodes: graphNodes,
    edges: graphEdges,
  }
}

export function findGreenEdges(nodes: UserJourneyNode[]): UserJourneyEdge[] {
  const startNodes = findAllStartNodes(nodes)

  const edges: UserJourneyEdge[] = []

  const makedNodesIds = new Set<UserJourneyNode['id']>()
  const traverse = (node: UserJourneyNode) => {
    if (makedNodesIds.has(node.id)) {
      return
    }
    makedNodesIds.add(node.id)
    let max = -Infinity
    let maxEdgeTarget = ''

    node.edges?.forEach((edge) => {
      const next = node.nexts?.[edge]
      if (next) {
        if (next.flows > max) {
          max = next.flows
          maxEdgeTarget = edge
        }
      }
    })

    if (maxEdgeTarget) {
      edges.push({
        source: node.id,
        target: maxEdgeTarget,
      })

      const targetNode = nodes.find((node) => node.id === maxEdgeTarget)

      if (targetNode && targetNode.edges?.length) {
        traverse(targetNode)
      }
    }
  }

  startNodes.forEach((node) => {
    traverse(node)
  })

  return edges
}

export function findAllStartNodes(nodes: UserJourneyNode[]): UserJourneyNode[] {
  const edges: UserJourneyEdge[] = []
  nodes.reduce((acc, node) => {
    node.edges?.forEach((edge) => {
      edges.push({
        source: node.id,
        target: edge,
      })
    })

    return acc
  }, edges)

  return nodes
    .filter((node) => {
      return !edges.some((edge) => {
        return edge.target === node.id
      })
    })
    .filter((node) => {
      return node.edges?.length
    })
}

export function longestPath<T extends string = UserJourneyNode['id']>(
  data: UserJourneyNode[],
): T[] {
  const memo: Record<string, T[]> = {}
  const memoStartingStatus: Record<string, 'starting' | 'ended'> = {}

  function dfs(id: T): T[] {
    if (memo[id]) {
      return memo[id]!
    }
    memoStartingStatus[id] = 'starting'

    let longestPath: T[] = []

    const edges = data.find((n) => n.id === id)?.edges ?? []
    for (let index = 0; index < edges.length; index++) {
      const neighbor = edges[index]!

      if (memoStartingStatus[neighbor] === 'starting') {
        // eslint-disable-next-line no-continue
        continue
      }

      const path = dfs(neighbor as any)
      if (path.length > longestPath.length) {
        longestPath = path
      }
    }

    memo[id] = [id, ...longestPath]

    memoStartingStatus[id] = 'ended'

    return memo[id] ?? []
  }

  let longestPath: T[] = []

  data.forEach((node) => {
    const path = dfs(node.id as any)
    if (path.length > longestPath.length) {
      longestPath = path
    }
  })

  console.log('aaa longestPath', longestPath)
  return longestPath
}

export interface UserJourneyDataItem {
  pre_action?: string
  action: string
  next_action?: string
}

function getActionOrder(action: UserJourneyNode['id']): number {
  return Number(action.replace(/^action/, ''))
}

export function resolveUserJourneyData(options: {
  // 名称字典
  actionNameDictionary: null | Record<string, string>
  // 用户自定义的旅程配置
  user_journeys: null | UserJourneyDataItem[]
}): UserJourneyNode[] {
  const { actionNameDictionary = {}, user_journeys = [] } = options

  const _namedActions = Object.keys(actionNameDictionary ?? [])
  const actions = _namedActions

  user_journeys?.forEach((item) => {
    const actionItems = [item.pre_action, item.action, item.next_action]

    actionItems.forEach((item) => {
      if (item && !actions.includes(item)) {
        actions.push(item)
      }
    })
  })

  const _action = [
    ...actions.sort((a, b) => getActionOrder(a) - getActionOrder(b)),
  ]
  return _action.map<UserJourneyNode>((action, index) => {
    const nextAction = _action[index + 1]
    let edges: UserJourneyNode['edges'] = []

    // 用户没有配置过时候，默认展示的15个节点，连起来
    if (!user_journeys || user_journeys.length === 0) {
      edges = nextAction ? [nextAction] : undefined
    } else {
      edges = user_journeys?.reduce<Required<UserJourneyNode>['edges']>(
        (edgesResult, item) => {
          if (item.pre_action && item.pre_action === action) {
            edgesResult.push(item.action)
          }

          if (item.action === action && item.next_action) {
            edgesResult.push(item.next_action)
          }

          return edgesResult
        },
        [],
      )
    }

    const order = getActionOrder(action)
    return {
      label: actionNameDictionary?.[action] ?? action,
      id: action,
      order: isNaN(order) ? undefined : order,

      edges,
    }
  })
}

/**
 * format the string
 * @param {string} str The origin string
 * @param {number} maxWidth max width
 * @param {number} fontSize font size
 * @return {string} the processed result
 */
export const fittingString = (
  str: string,
  maxWidth: number,
  fontSize: number,
): string => {
  const ellipsis = '...'
  const ellipsisLength = Util.getTextSize(ellipsis, fontSize)[0]
  let currentWidth = 0
  let res = str
  const pattern = new RegExp('[\u4E00-\u9FA5]+') // distinguish the Chinese charactors and letters
  str.split('').forEach((letter, i) => {
    if (currentWidth > maxWidth - ellipsisLength) return
    if (pattern.test(letter)) {
      // Chinese charactors
      currentWidth += fontSize
    } else {
      // get the width of single letter according to the fontSize
      currentWidth += Util.getLetterWidth(letter, fontSize)
    }
    if (currentWidth > maxWidth - ellipsisLength) {
      res = `${str.substr(0, i)}${ellipsis}`
    }
  })

  return res
}
